// exportResult.ts
//
// Builds ZIPs matching Swift's ResultWriter structure.
// Called from PostScanPage after one or more scans complete.
//
// ZIP structure per scan:
//   {sessionId}.zip
//     ├── json_data.json
//     ├── images/
//     │   └── image-{n}_{uuid}.png
//     └── strip_images/
//         └── strip-{i}-{n}_{uuid}.png

import JSZip from 'jszip';
import {
  serializeAnalysisResult,
  clearPmfStoryImages,
  revokeStoryImageUrls,
} from '@novarumdx/ndx-imaging-web';
import type { ResolvedFrameData, StripAnalysis, EngineConfig, TestConfig } from '@novarumdx/ndx-imaging-web';

export interface CompletedScan {
  sessionId: string;
  strips: StripAnalysis[];
  engineConfig: EngineConfig;
  /** testConfig from the unified JSON — present when loaded via unified path. */
  testConfig?: TestConfig;
  pmfStory: ResolvedFrameData[];
  analysisMs: number | null;
  completedAt: number; // Date.now() timestamp for display
}

// ─── Internal ZIP builder ─────────────────────────────────────────────────────

async function buildZip(scan: CompletedScan): Promise<Blob> {
  const zip = new JSZip();

  // 1. json_data.json — full envelope
  zip.file(
    'json_data.json',
    serializeAnalysisResult(scan.engineConfig, scan.strips, scan.pmfStory, {}, scan.testConfig),
  );

  // 2. Images — read from blob URLs (already resolved from IndexedDB)
  //    IndexedDB key format: "{sessionId}/images/image-N_uuid.png"
  //    Strip the leading "{sessionId}/" to get the path inside the ZIP.
  for (const frame of scan.pmfStory) {
    if (frame.imageObjectUrl && frame.imageUrl) {
      const blob = await fetch(frame.imageObjectUrl).then((r) => r.blob());
      zip.file(frame.imageUrl.replace(`${scan.sessionId}/`, ''), blob);
    }

    if (frame.stripImageObjectUrls && frame.stripImageUrls) {
      for (let i = 0; i < frame.stripImageObjectUrls.length; i++) {
        const blobUrl = frame.stripImageObjectUrls[i];
        const key = frame.stripImageUrls[i];
        if (blobUrl && key) {
          const blob = await fetch(blobUrl).then((r) => r.blob());
          zip.file(key.replace(`${scan.sessionId}/`, ''), blob);
        }
      }
    }
  }

  return zip.generateAsync({ type: 'blob' });
}

// ─── Trigger browser download ─────────────────────────────────────────────────

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Download a single scan as a ZIP, then clean up its IndexedDB images + blob URLs.
 */
export async function downloadScanZip(scan: CompletedScan): Promise<void> {
  const zipBlob = await buildZip(scan);
  triggerDownload(zipBlob, `${scan.sessionId}.zip`);
  // Blob URLs intentionally NOT revoked here — user can re-download.
  // They are revoked in deleteScan() when user explicitly removes the scan.
  await clearPmfStoryImages(scan.sessionId);
}

/**
 * Download all scans as individual ZIPs sequentially, then clean up.
 *
 * All ZIPs are built before any IDB is cleared. On Safari, running a readwrite
 * transaction on the IDB store (even for a different session's keys) can
 * invalidate lazy blob URL references across the entire store — so clearing
 * scan N's IDB would cause scan N+1's fetch() calls to fail with "load failed".
 * Building everything first avoids this entirely.
 */
export async function downloadAllScanZips(scans: CompletedScan[]): Promise<void> {
  // Phase 1: build all ZIPs while all IDB data is intact
  const built: Array<{ blob: Blob; scan: CompletedScan }> = [];
  for (const scan of scans) {
    built.push({ blob: await buildZip(scan), scan });
  }

  // Phase 2: trigger downloads and clear IDB
  for (const { blob, scan } of built) {
    triggerDownload(blob, `${scan.sessionId}.zip`);
    await clearPmfStoryImages(scan.sessionId);
  }
}

/**
 * Delete a scan from memory — revoke its blob URLs and clear its IndexedDB images.
 * Call this when the user removes a scan without downloading.
 */
export async function deleteScan(scan: CompletedScan): Promise<void> {
  revokeStoryImageUrls(scan.pmfStory);
  await clearPmfStoryImages(scan.sessionId);
}
